import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

const categoryImages: Record<string, string> = {
  shoes: 'https://readdy.ai/api/search-image?query=A%20pair%20of%20cream%20white%20canvas%20sneakers%20with%20natural%20jute%20rope%20soles%20placed%20on%20warm%20terracotta%20toned%20linen%20fabric%2C%20sunlit%20Mediterranean%20terrace%20setting%20with%20soft%20shadows%20from%20olive%20branches%2C%20editorial%20product%20photography%20with%20organic%20textures%20and%20golden%20hour%20light%2C%20rustic%20elegant%20aesthetic&width=600&height=500&seq=cat-shoes-2026-v2&orientation=landscape',
  hoodies: 'https://readdy.ai/api/search-image?query=Oversized%20dusty%20rose%20pink%20hoodie%20draped%20over%20a%20wooden%20chair%20on%20a%20sunlit%20balcony%20with%20terracotta%20tiles%2C%20warm%20afternoon%20light%20casting%20long%20shadows%2C%20Mediterranean%20lifestyle%20photography%2C%20soft%20and%20dreamy%20aesthetic%20with%20blurred%20greenery%20background&width=600&height=500&seq=cat-hoodies-2026-v2&orientation=landscape',
  tshirts: 'https://readdy.ai/api/search-image?query=Organic%20cotton%20sage%20green%20t-shirt%20hanging%20on%20a%20wooden%20clothesline%20against%20a%20whitewashed%20Mediterranean%20wall%2C%20gentle%20sea%20breeze%20movement%20visible%2C%20soft%20natural%20morning%20light%2C%20slow%20living%20aesthetic%2C%20minimalist%20coastal%20lifestyle%20photography&width=600&height=500&seq=cat-tshirts-2026-v2&orientation=landscape',
  jackets: 'https://readdy.ai/api/search-image?query=Lightweight%20caramel%20brown%20suede%20jacket%20displayed%20on%20a%20vintage%20wooden%20mannequin%20against%20warm%20stucco%20wall%2C%20soft%20golden%20afternoon%20light%20streaming%20through%20linen%20curtains%2C%20artisanal%20fashion%20photography%2C%20warm%20earthy%20tones%20with%20gentle%20textural%20contrast&width=600&height=500&seq=cat-jackets-2026-v2&orientation=landscape',
  pants: 'https://readdy.ai/api/search-image?query=Wide%20leg%20cream%20linen%20trousers%20laid%20flat%20on%20a%20woven%20rattan%20surface%20with%20dappled%20sunlight%20filtering%20through%20palm%20leaves%2C%20tropical%20resort%20aesthetic%2C%20soft%20natural%20lighting%20with%20organic%20shadow%20patterns%2C%20relaxed%20luxury%20fashion%20photography&width=600&height=500&seq=cat-pants-2026-v2&orientation=landscape',
  headwear: 'https://readdy.ai/api/search-image?query=Woven%20straw%20panama%20hat%20with%20a%20silk%20ribbon%20band%20resting%20on%20a%20vintage%20leather%20suitcase%2C%20seaside%20terrace%20backdrop%20with%20soft%20ocean%20blur%2C%20golden%20hour%20coastal%20light%2C%20timeless%20travel%20aesthetic%2C%20refined%20summer%20fashion%20photography&width=600&height=500&seq=cat-headwear-2026-v2&orientation=landscape',
};

export default function CategoryGrid() {
  const { t } = useTranslation();

  const featuredIds = ['shoes', 'hoodies', 'tshirts', 'jackets', 'pants', 'headwear'];

  return (
    <section id="categories" className="w-full py-20 md:py-28 px-6 md:px-10 bg-background-50">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            {t('categories.title')}
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-xl">
            {t('categories.subtitle')}
          </p>
        </div>

        {/* Featured Grid (3 cols) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {featuredIds.map((id) => {
            const cat = categories.find((c) => c.id === id);
            if (!cat) return null;
            return (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group block bg-background-50 rounded-2xl border border-background-200/60 overflow-hidden hover:border-background-300 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-56 overflow-hidden bg-background-100">
                  <img
                    src={categoryImages[id] || ''}
                    alt={t(cat.nameKey)}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900">
                      {t(cat.nameKey)}
                    </h3>
                    <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-500 transition-colors w-5 h-5 flex items-center justify-center"></i>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        {/* All Categories Pills */}
        <div className="flex flex-wrap gap-2.5">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 border border-background-200/50 text-foreground-700 font-label text-sm hover:bg-primary-50 hover:border-primary-200 hover:text-primary-700 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t(cat.nameKey)}
              <i className="ri-arrow-right-up-line text-xs w-3.5 h-3.5 flex items-center justify-center opacity-60"></i>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}